/// <amd-module name="Build/Admin/Checks/Actions/ChecksActionCreator" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

import { TaskAgentPool, TaskAgentQueue } from "DistributedTask/Client/Contracts/TaskAgent";

import { ActionsHub } from "Build/Admin/Checks/Actions/ActionsHub";
import { StoresHub } from "Build/Admin/Checks/Stores/StoresHub";
import { NotificationActionCreator } from "Build/Admin/Common/Actions/NotificationActionCreator";
import { Permission, Telemetry, StoreStatus } from "Build/Admin/Common/Constants";
import { AgentPoolSource } from "Build/Admin/Common/Sources/AgentPoolSource";
import { AgentQueueSource } from "Build/Admin/Common/Sources/AgentQueueSource";
import { SecuritySource } from "Build/Admin/Common/Sources/SecuritySource";
import { NotificationType } from "Build/Admin/Common/Stores/NotificationStore";

export interface IChecksActionCreatorOptions {
    pageContext: IVssPageContext;
    actionsHub: ActionsHub;
    agentPoolSource: AgentPoolSource;
    agentQueueSource: AgentQueueSource;
    securitySource: SecuritySource;
    storesHub: StoresHub;
}

export class ChecksActionCreator {
    public notificationActionCreator: NotificationActionCreator;

    private _actionsHub: ActionsHub;
    private _agentPoolSource: AgentPoolSource;
    private _agentQueueSource: AgentQueueSource;
    private _securitySource: SecuritySource;
    private _storesHub: StoresHub;
    private _pageContext: IVssPageContext;

    constructor(options: IChecksActionCreatorOptions) {
        this._actionsHub = options.actionsHub;
        this._agentPoolSource = options.agentPoolSource;
        this._agentQueueSource = options.agentQueueSource;
        this._securitySource = options.securitySource;
        this._storesHub = options.storesHub;
        this._pageContext = options.pageContext;

        this.notificationActionCreator = new NotificationActionCreator({ notificationActions: this._actionsHub.notificationActions });
    }

    public initializeCheckData(): void {
        const selectedAgentQueue: TaskAgentQueue | undefined = this._agentQueueSource.getCachedSelectedAgentQueue();
        const selectedAgentPool: TaskAgentPool | undefined = this._agentPoolSource.getCachedSelectedAgentPool();

        if (selectedAgentQueue) {
            this._actionsHub.selectedAgentQueueUpdated.invoke(selectedAgentQueue);
            this._actionsHub.selectedAgentPoolUpdated.invoke(selectedAgentPool);

            // Initialized data provider data
            this._fetchCheckData(selectedAgentQueue.id);
            this._fetchSecurityData(selectedAgentQueue.id);
        }
    }

    private _fetchCheckData(queueId: number): void {
        if (this._storesHub.checksStore.state.status === StoreStatus.None) {
            this._actionsHub.checkStoreStatusUpdated.invoke(StoreStatus.Loading);
            const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
            contributionService.getDataAsync("ms.vss-pipelinechecks.checks-data-provider", {"queueId": queueId}, true)
                .then(() => this._actionsHub.checkStoreStatusUpdated.invoke(StoreStatus.Loaded))
                .catch(this._handleError);
        }
    }

    private _fetchSecurityData(securityObjectId: number): void {
        if (this._storesHub.securityStore.state.status === StoreStatus.None) {
            this._actionsHub.securityStoreStatusUpdated.invoke(StoreStatus.Loading);
            const hasAgentPoolManagePermission: boolean = this._securitySource.hasAgentQueuePermission(securityObjectId, Permission.Manage);
            this._actionsHub.managePermissionUpdated.invoke(hasAgentPoolManagePermission);
        }
    }

    // tslint:disable-next-line:no-any
    private _handleError(reason: any): void {
        if (reason && reason.message) {
            this.notificationActionCreator.addNotification(reason.message, NotificationType.error);
            const service = this._pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
            service.publishEvent(Telemetry.Area, Telemetry.PoolsAdminFeature, {
                message: reason.message,
                error: reason
            });
        }
    }
}
